import React from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { getCommunityNotificationRecipients } from 'apis/CommunityAPI';
import { isCommunityCCRC } from 'components/Community/utils';
import ButtonPill from 'components/lib/ButtonPill';
import InputMultipleEmails from 'components/lib/InputMultipleEmails';
import { EMAIL_NOTIFICATION_TYPE } from 'components/Settings/constants';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { canAccessSalesBattlecardContent, canViewPricingData, isManagedBySuperuser } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import { getTabDataOrDefault, useShareViaEmailContext } from '../ShareViaEmailContext';
import ShareViaEmailExistingUsers from '../ShareViaEmailExistingUsers';
import { User } from '../types';

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 670px;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 30px;
  padding-bottom: 10px;
`;

const TabsRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 20px;
  margin-bottom: 10px;
`;

const Label = styled.h1`
  font-size: 14px;
  color: #222;
  margin-top: 27px;
  margin-bottom: 5px;
`;

const Paragraph = styled.p`
  font-size: 12px;
  color: var(--gray-dark);
`;

const SendEmailNotificationMainContent = () => {
  const {
    notificationType,
    notificationsTabData,
    users,
    setNotificationType,
    updateNotificationTabData,
    handleUserSearch,
  } = useShareViaEmailContext();

  const currentTabData = getTabDataOrDefault(notificationsTabData?.[notificationType]);

  const { emails, selectedUsers } = currentTabData;

  const { communityId, community } = useCommunityViewContext();
  const isCommunityCcrc = isCommunityCCRC(community?.rental_types || []);
  const { currentUser } = useAppSelector((state) => state.auth);
  const showPricingData = canViewPricingData(currentUser);
  const showMarketIntelligenceDigest = showPricingData;
  const showSalesBattlecardDigest = showPricingData && !isCommunityCcrc && canAccessSalesBattlecardContent(currentUser);

  const { isLoading, isError } = useQuery<User[]>(
    ['communityNotificationRecipients', communityId, notificationType],
    () => getCommunityNotificationRecipients(communityId, notificationType),
    {
      enabled: !!communityId && isManagedBySuperuser(currentUser),
      onSuccess: (response) => {
        const recipientIds = response.map((u) => u.id);
        const matchedUsers = users.filter((u) => recipientIds.includes(u.id));

        const currentSelected = notificationsTabData?.[notificationType]?.selectedUsers ?? [];
        if (currentSelected.length === 0) {
          updateNotificationTabData(notificationType, { selectedUsers: matchedUsers });
        }
      },
      onError: (error) => {
        console.error('Failed to load notification recipients:', error);
      },
    }
  );

  const handleTabChange = (notificationType: number) => {
    setNotificationType(notificationType);
    handleUserSearch({ target: { value: '' } });
  };

  const tabTitleMap: Record<number, string> = {
    [EMAIL_NOTIFICATION_TYPE.MARKET_INTELLIGENCE_DIGEST]: 'Manually Send Market Intelligence Digest',
    [EMAIL_NOTIFICATION_TYPE.ONLINE_REVIEWS_DIGEST]: 'Manually Send Online Review Digest',
    [EMAIL_NOTIFICATION_TYPE.SALES_BATTLECARD_DIGEST]: 'Manually Send Sales Battlecard Digest',
  };

  const title = tabTitleMap[notificationType] || 'Manually Send Notification';

  return (
    <Container>
      <Content>
        <TabsRow>
          {showMarketIntelligenceDigest && (
            <ButtonPill
              type={notificationType === EMAIL_NOTIFICATION_TYPE.MARKET_INTELLIGENCE_DIGEST ? 'primary' : 'default'}
              onClick={() => handleTabChange(EMAIL_NOTIFICATION_TYPE.MARKET_INTELLIGENCE_DIGEST)}
              style={{ height: 26, fontSize: 12, border: 'none', width: 165 }}
            >
              Market Intelligence Digest
            </ButtonPill>
          )}

          <ButtonPill
            type={notificationType === EMAIL_NOTIFICATION_TYPE.ONLINE_REVIEWS_DIGEST ? 'primary' : 'default'}
            onClick={() => handleTabChange(EMAIL_NOTIFICATION_TYPE.ONLINE_REVIEWS_DIGEST)}
            style={{ height: 26, fontSize: 12, border: 'none', width: 140 }}
          >
            Online Review Digest
          </ButtonPill>

          {showSalesBattlecardDigest && (
            <ButtonPill
              type={notificationType === EMAIL_NOTIFICATION_TYPE.SALES_BATTLECARD_DIGEST ? 'primary' : 'default'}
              onClick={() => handleTabChange(EMAIL_NOTIFICATION_TYPE.SALES_BATTLECARD_DIGEST)}
              style={{ height: 26, fontSize: 12, border: 'none', width: 150 }}
            >
              Sales Battlecard Digest
            </ButtonPill>
          )}
        </TabsRow>

        <Title>{title}</Title>
        <Paragraph>
          Standard email notifications are sent automatically, as set up in{' '}
          <span style={{ color: 'var(--text-primary)' }}>Settings - Email Notifications</span>. However, you can
          override this and manually send them from here at any time by making a selection of the notification type
          above.
        </Paragraph>

        <Label>New Email Addresses</Label>
        <InputMultipleEmails
          emails={emails}
          setEmails={(newEmails: string[]) => updateNotificationTabData(notificationType, { emails: newEmails })}
        />

        {isLoading && <p>Loading recipients...</p>}
        {isError && <p style={{ color: 'red' }}>Failed to load recipients</p>}

        <ShareViaEmailExistingUsers
          users={users}
          selectedUsers={selectedUsers}
          maxHeight="235px"
          onSelect={(user) =>
            updateNotificationTabData(notificationType, {
              selectedUsers: [...selectedUsers, user],
            })
          }
          onDeselect={(user) =>
            updateNotificationTabData(notificationType, {
              selectedUsers: selectedUsers.filter((u) => u.id !== user.id),
            })
          }
          onSelectAll={() =>
            updateNotificationTabData(notificationType, {
              selectedUsers: selectedUsers.length === users.length ? [] : [...users],
            })
          }
          onSearch={handleUserSearch}
        />
      </Content>
    </Container>
  );
};

export default SendEmailNotificationMainContent;
